import type { Article } from "../articles";
import { PRICING_2026 } from "../guides-data";

const ins = PRICING_2026.insulation;

export const articleHowMuchAtticInsulationDoYouNeed: Article = {
  slug: "how-much-attic-insulation-do-you-need",
  title: "Attic Insulation: How Much You Need, What It Costs, and How to Check What You Have",
  description: "The correct attic insulation R-value depends on your climate zone, not a single national standard. Here's how to check your current level, calculate what you need, and compare material options.",
  category: "Insulation",
  tags: ["Insulation", "Energy Saving", "DIY Tips", "Home Maintenance"],
  createdAt: "June 22, 2026",
  updatedAt: "July 3, 2026",
  lastVerified: "July 3, 2026",
  readTime: "9 min read",
  keyTakeaway: "The DOE recommends R-38 to R-60 for most U.S. attics depending on climate zone, roughly 12–18 inches of blown-in fiberglass or cellulose. The correct figure is zone-specific: southern zone 1–2 homes need R-30 to R-38; northern zone 5–7 homes need R-49 to R-60. Attic insulation is one of the highest-return energy efficiency upgrades because the attic floor is the primary path for heat gain and loss in most homes.",
  author: { name: "Mauricio L.", url: "/authors/mauricio-l/" },
  sources: [
    {
      name: "U.S. Department of Energy - Recommended Home Insulation R-Values by Climate Zone",
      url: "https://www.energy.gov/energysaver/insulation"
    },
    {
      name: "North American Insulation Manufacturers Association (NAIMA) - Insulation Facts",
      url: "https://insulationinstitute.org"
    }
  ],
  keyMetrics: [
    { label: "DOE Target R-Value", value: "R-38 to R-60 (by zone)" },
    { label: "Fiberglass Cost / SF", value: `$${ins.costPerSqFt.fiberglass.min.toFixed(2)}-$${ins.costPerSqFt.fiberglass.max.toFixed(2)}` },
    { label: "Cellulose Cost / SF", value: `$${ins.costPerSqFt.cellulose.min.toFixed(2)}-$${ins.costPerSqFt.cellulose.max.toFixed(2)}` },
    { label: "Typical Thickness", value: "12-18 Inches" }
  ],
  faqs: [
    {
      question: "Do radiant barriers replace traditional attic insulation?",
      answer: "No. Radiant barriers (reflective foil sheets stapled to the underside of roof rafters) reflect radiant heat from the sun and reduce attic air temperature, but they don't prevent conductive heat transfer from the hot attic into living spaces below. Per DOE guidance, radiant barriers work best in hot, sunny climates as a supplement to, not a replacement for, bulk insulation on the attic floor. You still need R-38 to R-60 of fiberglass or cellulose on the attic floor."
    },
    {
      question: "Can I add new insulation over old insulation?",
      answer: "Generally yes, if the existing insulation is dry and not compressed. Blown-in cellulose or fiberglass can be added directly over existing batts: the new layer runs perpendicular to the old to cover gaps between batt edges. Compressed insulation has reduced R-value (the insulating effect comes from trapped air, not material density), so new material should not compact existing layers. If you find moisture-damaged or molded insulation, it needs to be removed and the moisture source addressed before new insulation goes in."
    }
  ],
  content: `
    <p>
      The attic floor is where most homes lose the most heating and cooling energy. Heat moves through the path of least resistance, and an under-insulated attic creates a large, low-resistance path between conditioned living space and unconditioned attic air. In summer, attic temperatures regularly exceed 130°F in many U.S. climates; without adequate insulation on the floor, that heat conducts directly into ceiling assemblies and living areas.
    </p>
    <p>
      The DOE recommends specific R-value targets by climate zone, not a single national number. Whether your home needs R-30 or R-60 depends on where you live. This article covers how to determine what you have, what you need, and what it costs to get there.
    </p>

    <div class="my-6 p-4 rounded-lg bg-surface border border-border-custom">
      <p class="font-bold text-foreground m-0 mb-2">The Short Version</p>
      <p class="text-sm text-text-muted m-0">
        The DOE recommends R-38 to R-60 for most U.S. attics, or about 12–18 inches of blown-in material. Fiberglass blown-in costs $${ins.costPerSqFt.fiberglass.min}–$${ins.costPerSqFt.fiberglass.max}/SF installed; cellulose costs $${ins.costPerSqFt.cellulose.min}–$${ins.costPerSqFt.cellulose.max}/SF. Zone 1–2 (Florida, Gulf Coast, Southwest): R-30 to R-38. Zones 3–4 (Mid-Atlantic, Midwest): R-38 to R-49. Zones 5–7 (Northeast, northern Midwest): R-49 to R-60. Check your zone at energy.gov.
      </p>
      <p class="text-xs text-text-muted mt-2 m-0">
        <em>R-value recommendations per U.S. DOE Insulation Recommendations by Zone. Cost ranges from Meazora 2026 installer data.</em>
      </p>
    </div>

    <h2>What this article covers:</h2>
    <ul>
      <li>DOE R-value targets by climate zone</li>
      <li>How to inspect your attic and measure what you currently have</li>
      <li>Material comparison: fiberglass batts, blown-in cellulose, and spray foam</li>
      <li>How to calculate how much material to buy</li>
    </ul>

    <h2>DOE R-Value Targets by Climate Zone</h2>
    <p>
      The Department of Energy publishes recommended attic insulation R-values by climate zone. These are minimum recommendations for existing homes that are under-insulated:
    </p>
    <ul>
      <li><strong>Zone 1–2</strong> (Florida, southern Texas, Hawaii, southern California): R-30 to R-38</li>
      <li><strong>Zone 3–4</strong> (Mid-Atlantic, most of the Southeast, Midwest): R-38 to R-49</li>
      <li><strong>Zone 5–7</strong> (Northeast, northern Midwest, mountain states): R-49 to R-60</li>
      <li><strong>Zone 8</strong> (Alaska, northern Minnesota): R-60+</li>
    </ul>
    <p>
      To confirm your zone, enter your ZIP code at energycodes.gov or the DOE's insulation page at energy.gov/energysaver/insulation. The target for your zone is the total R-value after adding new insulation over whatever you already have. To identify where your home is losing energy, check if <a href="/articles/is-home-energy-audit-worth-it/" class="text-[var(--accent)] hover:underline font-semibold">a home energy audit is worth it</a>.
    </p>

    <h2>DIY Attic Inspection: What to Check Before Ordering Material</h2>
    <p>
      Grab a tape measure, a bright flashlight, and a dust mask. Work from the attic access hatch or pull-down stairs: you don't need to walk the full attic to get a useful assessment.
    </p>
    <div class="bg-surface p-6 rounded-xl border border-border-custom my-6 shadow-sm">
      <ul class="space-y-4 m-0 pl-0 list-none">
        <li class="flex items-start gap-3">
          <input type="checkbox" class="mt-1" disabled checked />
          <div>
            <strong>Measure thickness:</strong> Push a tape measure through the insulation to the attic floor. Note the depth in inches. Old fiberglass batts from the 1970s–80s may be 6 inches thick but only R-11, thickness alone doesn't tell you R-value.
          </div>
        </li>
        <li class="flex items-start gap-3">
          <input type="checkbox" class="mt-1" disabled />
          <div>
            <strong>Check coverage at the eaves:</strong> Insulation is often thick near the center and thin or absent near the eave ends. Thermal bypasses at the perimeter can significantly undermine attic performance.
          </div>
        </li>
        <li class="flex items-start gap-3">
          <input type="checkbox" class="mt-1" disabled />
          <div>
            <strong>Look for air bypasses:</strong> Shine your light along wall-to-attic connections, around recessed light fixtures, and at any penetrations (pipes, wires, ducts). Open penetrations transfer more heat than thin insulation. (Calculating load changes for new heating/cooling equipment? Check our <a href="/calculators/hvac/" class="text-[var(--accent)] hover:underline font-semibold">HVAC replacement cost calculator</a>).
          </div>
        </li>
        <li class="flex items-start gap-3">
          <input type="checkbox" class="mt-1" disabled />
          <div>
            <strong>Check for moisture damage:</strong> Dark spots, staining, or matted insulation indicate a leak or condensation issue. Wet or compressed insulation has significantly reduced R-value and needs to be removed and replaced after the moisture source is corrected.
          </div>
        </li>
        <li class="flex items-start gap-3">
          <input type="checkbox" class="mt-1" disabled />
          <div>
            <strong>Verify soffit vent clearance:</strong> Insulation should not block soffit ventilation. Rafter baffles (cardboard or foam channels) maintain airflow from the soffit to the ridge vent, confirm they're in place before adding insulation.
          </div>
        </li>
      </ul>
    </div>

    <h2>Material Options: Fiberglass, Cellulose, and Spray Foam</h2>
    <p>
      <strong>Blown-in fiberglass ($${ins.costPerSqFt.fiberglass.min}–$${ins.costPerSqFt.fiberglass.max}/SF installed):</strong> The most common professional installation for attic floor insulation. Machine-blown fiberglass fills irregular spaces better than batts and achieves consistent R-value per inch (approximately R-2.5 per inch). Does not settle significantly over time. Professional installation recommended for achieving uniform coverage.
    </p>
    <p>
      <strong>Blown-in cellulose ($${ins.costPerSqFt.cellulose.min}–$${ins.costPerSqFt.cellulose.max}/SF installed):</strong> Made from recycled paper treated with borate for fire and pest resistance. Achieves approximately R-3.5 per inch. Cellulose settles over time, typically 15–20% within the first year, which is accounted for by installing to a higher initial depth. Per NAIMA, properly installed and settled cellulose still meets its rated R-value. DIY-friendly with rented blower equipment; available at most home centers.
    </p>
    <p>
      <strong>Spray foam ($6–$12/SF):</strong> Provides both air sealing and insulation in one application. Open-cell spray foam achieves approximately R-3.5 per inch; closed-cell achieves R-6 to R-7 per inch. The primary use case in attics is air sealing penetrations and bypasses rather than whole-attic coverage: the cost per SF makes it impractical for full attic floor coverage in most situations. Requires professional installation.
    </p>

    <!--COMPONENT:info-note|label="Worth Knowing"-->
    Insulation material age affects performance. Old fiberglass batts from pre-1980 construction may have been manufactured at lower density than current products. A 6-inch batt from that era may only be R-11, not the R-19 a modern 6-inch high-density batt achieves. If you're unsure of your existing insulation's rated R-value, a home energy auditor can test it, or you can identify the product by its color and condition and look up the original manufacturer's spec.
    <!--ENDOFCOMPONENT-->

    <h2>Calculating How Much Material to Buy</h2>
    <p>
      Start with your attic's square footage (length × width, minus any obstructions). Then check the coverage table on the insulation bag for the target R-value. A typical bag of blown-in fiberglass covers:
    </p>
    <ul>
      <li>At R-30: approximately 40–50 SF per bag</li>
      <li>At R-38: approximately 30–35 SF per bag</li>
      <li>At R-49: approximately 25–30 SF per bag</li>
    </ul>
    <p>
      For a 1,500 SF attic targeting R-38 from zero, you'll need approximately 45–50 bags of blown-in fiberglass. If you already have R-19 and are adding to reach R-38, you're buying for the difference, approximately half that quantity. Order 10% extra to account for settling and irregular areas.
    </p>
    <p>
      Use our <a href="/calculators/" class="text-accent underline decoration-accent/30 hover:decoration-accent/60 transition-colors">remodeling calculators</a> to estimate bag counts and total material cost based on your attic's square footage and target R-value.
    </p>
  `
};